// PrintPayslip.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { format } from "date-fns";
import { graphqlQuery } from "../../../../data/Axios/DynamicService";
import AGO_Logo from "../../../../Assets/Images/AGO-Logo.png";
import { companyManagementUrl } from "../../../../config";

const baseURL = process.env.REACT_APP_API_URL || 'https://matrix.mcb.co.ke/usermanagement';
const baseURL2 = companyManagementUrl.uri;

const LOGO_ORIGIN = (() => {
  try {
    return new URL(baseURL2).origin;
  } catch {
    return 'https://matrix.mcb.co.ke';
  }
})();
 
// ---- Logo path normalization (identical logic to PrintInvoice.jsx, ----
// ---- except it resolves against LOGO_ORIGIN instead of baseURL)     ----
const normalizeLogoPath = (rawPath) => {
  if (!rawPath || typeof rawPath !== 'string') return null;
 
  const trimmed = rawPath.trim();
  if (!trimmed) return null;
 
  const isWindowsPath = /^[a-zA-Z]:[\\/]/.test(trimmed);
  if (isWindowsPath) return null;
 
  let cleaned = trimmed.replace(/^(\.\.\/|\.\/)+/, '');
  cleaned = cleaned.replace(/^\/+/, '');
 
  if (!cleaned.startsWith('assets/')) {
    return null;
  }
 
  return `${LOGO_ORIGIN.replace(/\/+$/, '')}/${cleaned}`;
};


// ---- Color extraction helpers ----
const DEFAULT_COLORS = { primary: "#8A3533", secondary: "#9EBB3C" };

const toHex = (r, g, b) =>
  `#${[r, g, b].map((v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0")).join("")}`;

const colorDistance = (a, b) =>
  Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

const luminance = (hex) => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const [rl, gl, bl] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

const readableTextColor = (hex) => (luminance(hex) > 0.5 ? "#1a1a1a" : "#ffffff");

const extractColorsFromImage = (imgSrc) => {
  return new Promise((resolve) => {
    if (!imgSrc) return resolve(DEFAULT_COLORS);

    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const size = 60;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);

        const counts = {};
        for (let i = 0; i < data.length; i += 4) {
          const [r, g, b, a] = [data[i], data[i + 1], data[i + 2], data[i + 3]];
          if (a < 200) continue;
          const brightness = (r + g + b) / 3;
          if (brightness > 235 || brightness < 20) continue;

          const key = [
            Math.round(r / 16) * 16,
            Math.round(g / 16) * 16,
            Math.round(b / 16) * 16,
          ].join(",");
          counts[key] = (counts[key] || 0) + 1;
        }

        const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
        if (sorted.length === 0) return resolve(DEFAULT_COLORS);

        const primaryRgb = sorted[0][0].split(",").map(Number);
        const primary = toHex(...primaryRgb);

        let secondary = DEFAULT_COLORS.secondary;
        for (let i = 1; i < sorted.length; i++) {
          const rgb = sorted[i][0].split(",").map(Number);
          if (colorDistance(primaryRgb, rgb) > 90) {
            secondary = toHex(...rgb);
            break;
          }
        }

        resolve({ primary, secondary });
      } catch (err) {
        console.error("Color extraction failed:", err);
        resolve(DEFAULT_COLORS);
      }
    };
    img.onerror = () => resolve(DEFAULT_COLORS);
    img.src = imgSrc;
  });
};
// -----------------------------------

const PrintPayslip = ({ data }) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = parseInt(decodedToken.CompanyId);

  const [companyData, setCompanyData] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const [brandColors, setBrandColors] = useState(DEFAULT_COLORS);

  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const companyQuery = `
          {
          allCompanies {
                id
                companyName
                companyEmail
                contactEmail
                contactEmailPassword
                emailDisplayName
                emailClient
                emailClientPortNumber
                subscriptionId
                createdBy
                createdDate
                updatedBy
                updatedDate
                logoPath
                address
                poBox
                phone
                mobile
                website
                pinVat
                tagline
            }
        }
        `;

        const response = await graphqlQuery(
          companyQuery,
          companyManagementUrl.uri,
        );

        if (response && response.length > 0) {
          const userCompanyId = companyId;
          const company = response.find(c => c.id === userCompanyId) 
                        || response[0];
          setCompanyData(company);
          console.log("Company Data1:", response[0]);

          const normalizedLogoUrl = normalizeLogoPath(company.logoPath);
          setLogoUrl(normalizedLogoUrl);
          setLogoLoadFailed(false);

          if (normalizedLogoUrl) {
            const colors = await extractColorsFromImage(normalizedLogoUrl);
            setBrandColors(colors);
          } else {
            setBrandColors(DEFAULT_COLORS);
          }
        }
      } catch (err) {
        console.error("Error fetching company data:", err);
      }
    };

    fetchCompanyData();
  }, []);

  const payslip = data?.[0] || {};
  const employeeName = payslip.employeeName;
  const primaryText = readableTextColor(brandColors.primary);

  const formatCurrency = (amt) =>
    new Intl.NumberFormat("en-KE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amt || 0);

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return format(new Date(iso), "dd/MM/yyyy");
    } catch {
      return iso;
    }
  };

  const totalEarnings = (payslip.earnings || []).reduce(
    (sum, e) => sum + (parseFloat(e.amount) || 0),
    0
  );

  const totalDeductions = (payslip.deductions || []).reduce(
    (sum, d) => sum + (parseFloat(d.amount) || 0),
    0
  );

  const totalEmployerContrib = (payslip.employerContributions || []).reduce(
    (sum, c) => sum + (parseFloat(c.employerAmount) || 0),
    0
  );

  const companyInfo = {
    name: companyData?.companyName || "set at company management",
    address: companyData?.address || "set at company management",
    poBox: companyData?.poBox || "set at company management",
    phone: companyData?.phone || "set at company management",
    mobile: companyData?.mobile || "set at company management",
    email: companyData?.companyEmail || "set at company management",
    website: companyData?.website || "set at company management",
    pinVat: companyData?.pinVat || "set at company management",
    tagline: companyData?.tagline || "set at company management",
  };

  const handleImageError = (e) => {
    setLogoLoadFailed(true);
    const img = e.target;
    img.src = AGO_Logo;
  };

  const printStyles = `
    @media print {
      body * { visibility: hidden; }
      .print-section, .print-section * { visibility: visible; }
      .print-section {
        position: absolute; left:0; top:0; width:100%; background:white;
        padding:0; margin:0; transform:scale(0.95); transform-origin:top left;
      }
      .no-print { display:none !important; }
      @page { margin:10mm; size:A4 portrait; }
    }

    .print-container { min-height:100vh; background:white; padding:20px; }
    .print-section {
      padding:25px; font-family:'Georgia','Times New Roman',serif;
      background:white; max-width:210mm; min-height:297mm;
      margin:0 auto; position:relative; border:3px double ${brandColors.primary};
      box-shadow:0 0 20px rgba(138,53,51,0.1); font-size:14px;
    }

    .header { text-align:center; margin-bottom:30px; padding-bottom:20px;
      border-bottom:3px double ${brandColors.primary}; }
    .logo { max-width:180px; height:auto; max-height:90px; object-fit:contain; display:block; margin:0 auto; }
    .logo-placeholder {
      width:180px; height:90px; background:linear-gradient(135deg,${brandColors.primary} 0%,${brandColors.secondary} 100%);
      display:flex; align-items:center; justify-content:center; border:3px double ${brandColors.primary};
      color:${primaryText}; font-weight:bold; font-size:18px; text-align:center; line-height:1.2;
      margin:0 auto;
    }
    .title { font-size:28px; font-weight:bold; color:${brandColors.primary}; margin:10px 0;
      text-transform:uppercase; letter-spacing:2px; }
    .subtitle { font-size:16px; color:${brandColors.secondary}; font-style:italic; margin-bottom:10px; }
    .firm { font-size:12px; color:#333; line-height:1.4; }

    .info-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; margin-bottom:25px; }
    .info-box { padding:15px; border:1px solid #ddd; border-radius:8px; background:#f9f9f9; }
    .info-title { font-weight:bold; color:${brandColors.primary}; margin-bottom:10px; font-size:14px; }
    .info-row { display:flex; justify-content:space-between; padding:4px 0;
      border-bottom:1px dotted #ccc; }
    .info-label { font-weight:bold; color:${brandColors.primary}; min-width:130px; }
    .info-value { color:#333; font-weight:500; text-align:right; }

    .pay-table { width:100%; border-collapse:collapse; border:2px solid ${brandColors.primary};
      margin:20px 0 30px; font-size:13px; background:white; box-shadow:0 2px 4px rgba(0,0,0,0.1); }
    .pay-table th {
      background:linear-gradient(135deg,${brandColors.primary} 0%,${brandColors.primary} 100%);
      color:${primaryText}; font-weight:bold; padding:12px 8px; border:1px solid ${brandColors.primary};
      text-align:left; font-size:12px; text-transform:uppercase; letter-spacing:1px;
    }
    .pay-table td { padding:10px 8px; border:1px solid #ddd; vertical-align:top; color:#334; }
    .pay-table tr:nth-child(even) td { background:#f9f9f9; color:#333; }
    .amt { text-align:right; color:#333; font-family:'Courier New',monospace; font-weight:600; }

    .section-title {
      font-size:16px; font-weight:bold; color:${brandColors.primary}; margin:25px 0 10px;
      border-bottom:2px solid ${brandColors.secondary}; padding-bottom:5px;
    }

    .summary { width:340px; margin-left:auto; margin-top:25px;
      border:2px solid ${brandColors.primary}; border-radius:8px; overflow:hidden;
      box-shadow:0 3px 6px rgba(0,0,0,0.1); }
    .summary td { padding:12px 15px; border-bottom:1px solid #ddd; }
    .sum-label { font-weight:bold; background:linear-gradient(135deg,#f9f9f9 0%,#e9e9e9 100%);
      color:${brandColors.primary}; font-size:13px; }
    .sum-value { text-align:right; font-family:'Courier New',monospace;
      font-weight:bold; color:#333; font-size:13px; }
    .total-row { background:linear-gradient(135deg,${brandColors.primary} 0%,${brandColors.primary} 100%);
      color:${primaryText} !important; font-size:16px; }
    .total-row .sum-value { color:${primaryText} !important; font-weight:800; font-size:16px; }

    .footer { margin-top:40px; padding-top:25px; border-top:3px double ${brandColors.secondary};
      text-align:center; font-size:11px; color:#666; }
    .thanks { font-size:16px; font-weight:bold; color:${brandColors.primary};
      margin-bottom:15px; font-style:italic; }
    .sig-area { margin-top:40px; display:grid; grid-template-columns:1fr 1fr;
      gap:40px; max-width:500px; margin-left:auto; margin-right:auto; }
    .sig-line { border-top:2px solid ${brandColors.primary}; width:100%; margin:0 auto;
      padding-top:8px; font-size:12px; color:${brandColors.primary}; font-weight:600; text-align:center; }
    .stamp { position:absolute; bottom:100px; right:50px; opacity:0.8; }
    .stamp-box { border:2px solid ${brandColors.primary}; border-radius:5px; padding:10px;
      transform:rotate(5deg); background:white; font-size:10px; font-weight:bold;
      color:${brandColors.primary}; text-align:center; }
    .footnote { margin-top:25px; font-size:10px; color:#999; font-style:italic; }
  `;

  return (
    <Box className="print-container">
      <style>{printStyles}</style>

      <Box className="print-section">
        <div className="header">
          <div style={{ marginBottom: "15px" }}>
            <img
              src={logoUrl && !logoLoadFailed ? logoUrl : AGO_Logo}
              alt={companyInfo.name}
              className="logo"
              onError={handleImageError}
            />
          </div>

          <div className="title">PAYSLIP</div>
          <div className="subtitle">{companyInfo.tagline}</div>

          <div className="firm">
            <div>{companyInfo.name}</div>
            <div>{companyInfo.address}</div>
            <div>{companyInfo.poBox}</div>
            <div>Tel: {companyInfo.phone} | Email: {companyInfo.email}</div>
            <div>PIN/VAT: {companyInfo.pinVat}</div>
          </div>
        </div>

        <div className="info-grid">
          <div className="info-box">
            <div className="info-title">EMPLOYEE DETAILS</div>
            <div className="info-row">
              <span className="info-label">Name:</span>
              <span className="info-value">{employeeName || "-"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">KRA PIN:</span>
              <span className="info-value">{payslip.kraPin || "-"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">NSSF NO:</span>
              <span className="info-value">{payslip.nssf || "-"}</span>
            </div>
            <div className="info-row">
              <span className="info-label">NHIF NO:</span>
              <span className="info-value">{payslip.nhif || "-"}</span>
            </div>
          </div>

          <div className="info-box">
            <div className="info-title">PAY PERIOD</div>
            <div className="info-row">
              <span className="info-label">Pay Date:</span>
              <span className="info-value">{formatDate(payslip.payDate)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Period:</span>
              <span className="info-value">
                {formatDate(payslip.periodStart)} – {formatDate(payslip.periodEnd)}
              </span>
            </div>
          </div>
        </div>

        <div className="section-title">EARNINGS</div>
        <TableContainer component={Paper} elevation={0}>
          <Table className="pay-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "40px" }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell style={{ width: "130px" }} className="amt">Amount (KES)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslip.earnings?.length > 0 ? (
                payslip.earnings.map((earn, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{earn.name}</TableCell>
                    <TableCell className="amt">{formatCurrency(earn.amount)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} style={{ textAlign: "center", fontStyle: "italic", color: "#666" }}>
                    No earnings recorded
                  </TableCell>
                </TableRow>
              )}
              <TableRow style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                <TableCell colSpan={2} style={{ textAlign: "right" }}>TOTAL EARNINGS</TableCell>
                <TableCell className="amt">{formatCurrency(totalEarnings)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <div className="section-title">TAX COMPUTATION</div>
        <TableContainer component={Paper} elevation={0}>
          <Table className="pay-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "40px" }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell style={{ width: "130px" }} className="amt">Amount (KES)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslip.taxComputation?.length > 0 ? (
                payslip.taxComputation.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell className="amt">{formatCurrency(item.amount)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} style={{ textAlign: "center", fontStyle: "italic", color: "#666" }}>
                    No tax computation data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div className="section-title">DEDUCTIONS</div>
        <TableContainer component={Paper} elevation={0}>
          <Table className="pay-table">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "40px" }}>#</TableCell>
                <TableCell>Description</TableCell>
                <TableCell style={{ width: "80px" }} className="amt">Statutory</TableCell>
                <TableCell style={{ width: "130px" }} className="amt">Amount (KES)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslip.deductions?.length > 0 ? (
                payslip.deductions.map((ded, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{ded.name}</TableCell>
                    <TableCell className="amt" style={{ fontWeight: ded.isStatutory ? "bold" : "normal" }}>
                      {ded.isStatutory ? "YES" : "NO"}
                    </TableCell>
                    <TableCell className="amt">{formatCurrency(ded.amount)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} style={{ textAlign: "center", fontStyle: "italic", color: "#666" }}>
                    No deductions recorded
                  </TableCell>
                </TableRow>
              )}
              <TableRow style={{ background: "#ffe6e6", fontWeight: "bold" }}>
                <TableCell colSpan={3} style={{ textAlign: "right" }}>TOTAL DEDUCTIONS</TableCell>
                <TableCell className="amt">{formatCurrency(totalDeductions)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {payslip.employerContributions?.length > 0 && (
          <>
            <div className="section-title">EMPLOYER CONTRIBUTIONS</div>
            <TableContainer component={Paper} elevation={0}>
              <Table className="pay-table">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: "40px" }}>#</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell style={{ width: "130px" }} className="amt">Amount (KES)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payslip.employerContributions.map((cont, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{cont.name}</TableCell>
                      <TableCell className="amt">{formatCurrency(cont.employerAmount || cont.amount)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow style={{ background: "#e6f7ff", fontWeight: "bold" }}>
                    <TableCell colSpan={2} style={{ textAlign: "right" }}>TOTAL</TableCell>
                    <TableCell className="amt">{formatCurrency(totalEmployerContrib)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        <div className="summary">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td className="sum-label">Total Earnings</td>
                <td className="sum-value">{formatCurrency(totalEarnings)}</td>
              </tr>
              <tr>
                <td className="sum-label">Total Deductions</td>
                <td className="sum-value">{formatCurrency(totalDeductions)}</td>
              </tr>
              {totalEmployerContrib > 0 && (
                <tr>
                  <td className="sum-label">Employer Contributions</td>
                  <td className="sum-value">{formatCurrency(totalEmployerContrib)}</td>
                </tr>
              )}
              <tr className="total-row">
                <td className="sum-label">NET PAY</td>
                <td className="sum-value">{formatCurrency(payslip.netPay)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="footer">
          <div className="thanks">Payment Acknowledgement</div>
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "20px" }}>
            This payslip is computer-generated and is valid without a signature.
          </div>

          <div className="sig-area">
            <div>
              <div className="sig-line">Employee Signature</div>
            </div>
            <div>
              <div className="sig-line">Authorized Signatory</div>
            </div>
          </div>

          <div className="stamp">
            <div className="stamp-box">
              PAID<br />
              {formatDate(payslip.payDate)}
            </div>
          </div>

          <div className="footnote">
            Printed on: {format(new Date(), "dd/MM/yyyy HH:mm")}
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default PrintPayslip;
